import type { TelephonyRoutingSnapshot } from './types';

export type RouteDiffField =
  | 'name'
  | 'strategy'
  | 'ringSeconds'
  | 'maxWaitSeconds'
  | 'fallbackQueueId'
  | 'isActive';

export type RouteDiffChange =
  | { kind: 'FIELD'; field: RouteDiffField; before: string | number | boolean | null; after: string | number | boolean | null }
  | { kind: 'MEMBER_ADDED'; memberId: string; afterPosition: number }
  | { kind: 'MEMBER_REMOVED'; memberId: string; beforePosition: number }
  | { kind: 'MEMBER_MOVED'; memberId: string; beforePosition: number; afterPosition: number };

export type RouteGroupDiff = {
  id: string;
  name: string;
  status: 'ADDED' | 'REMOVED' | 'CHANGED';
  changes: RouteDiffChange[];
};

export type RouteDiff = {
  mode: 'INITIAL' | 'UNCHANGED' | 'CHANGED';
  root: { beforeId: string | null; afterId: string | null } | null;
  groups: RouteGroupDiff[];
  totalChanges: number;
};

type RoutingGroup = TelephonyRoutingSnapshot['groups'][number];

const GROUP_FIELDS: RouteDiffField[] = [
  'name',
  'strategy',
  'ringSeconds',
  'maxWaitSeconds',
  'fallbackQueueId',
  'isActive',
];

function orderedMemberIds(group: RoutingGroup) {
  return [...group.members]
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map(member => member.telephonyUserId);
}

function compareGroup(before: RoutingGroup, after: RoutingGroup): RouteDiffChange[] {
  const changes: RouteDiffChange[] = [];
  for (const field of GROUP_FIELDS) {
    if (before[field] !== after[field]) {
      changes.push({ kind: 'FIELD', field, before: before[field], after: after[field] });
    }
  }

  const beforeMembers = orderedMemberIds(before);
  const afterMembers = orderedMemberIds(after);
  const beforeSet = new Set(beforeMembers);
  const afterSet = new Set(afterMembers);

  beforeMembers.forEach((memberId, index) => {
    if (!afterSet.has(memberId)) {
      changes.push({ kind: 'MEMBER_REMOVED', memberId, beforePosition: index + 1 });
    }
  });
  afterMembers.forEach((memberId, index) => {
    if (!beforeSet.has(memberId)) {
      changes.push({ kind: 'MEMBER_ADDED', memberId, afterPosition: index + 1 });
    }
  });

  const beforeCommon = beforeMembers.filter(memberId => afterSet.has(memberId));
  const afterCommon = afterMembers.filter(memberId => beforeSet.has(memberId));
  if (beforeCommon.some((memberId, index) => afterCommon[index] !== memberId)) {
    for (const memberId of beforeCommon) {
      const beforeCommonPosition = beforeCommon.indexOf(memberId);
      const afterCommonPosition = afterCommon.indexOf(memberId);
      if (beforeCommonPosition !== afterCommonPosition) {
        const beforePosition = beforeMembers.indexOf(memberId) + 1;
        const afterPosition = afterMembers.indexOf(memberId) + 1;
        if (beforePosition === afterPosition) continue;
        changes.push({
          kind: 'MEMBER_MOVED',
          memberId,
          beforePosition,
          afterPosition,
        });
      }
    }
  }

  return changes;
}

export function buildRouteDiff(
  applied: TelephonyRoutingSnapshot | null,
  draft: TelephonyRoutingSnapshot,
): RouteDiff {
  if (!applied) {
    const groups = draft.groups.map(group => ({
      id: group.id,
      name: group.name,
      status: 'ADDED' as const,
      changes: [],
    }));
    const root = draft.rootQueueId ? { beforeId: null, afterId: draft.rootQueueId } : null;
    return {
      mode: 'INITIAL',
      root,
      groups,
      totalChanges: groups.length + (root ? 1 : 0),
    };
  }

  const beforeById = new Map(applied.groups.map(group => [group.id, group] as const));
  const afterById = new Map(draft.groups.map(group => [group.id, group] as const));
  const groups: RouteGroupDiff[] = [];

  for (const group of applied.groups) {
    if (!afterById.has(group.id)) {
      groups.push({ id: group.id, name: group.name, status: 'REMOVED', changes: [] });
    }
  }
  for (const group of draft.groups) {
    const before = beforeById.get(group.id);
    if (!before) {
      groups.push({ id: group.id, name: group.name, status: 'ADDED', changes: [] });
      continue;
    }
    const changes = compareGroup(before, group);
    if (changes.length) groups.push({ id: group.id, name: group.name, status: 'CHANGED', changes });
  }

  const root = applied.rootQueueId === draft.rootQueueId
    ? null
    : { beforeId: applied.rootQueueId || null, afterId: draft.rootQueueId || null };
  const totalChanges = groups.reduce(
    (total, group) => total + (group.status === 'CHANGED' ? group.changes.length : 1),
    root ? 1 : 0,
  );

  return {
    mode: totalChanges ? 'CHANGED' : 'UNCHANGED',
    root,
    groups,
    totalChanges,
  };
}

export function routeSnapshotChain(snapshot: TelephonyRoutingSnapshot) {
  const groups = new Map(snapshot.groups.map(group => [group.id, group] as const));
  const chain: RoutingGroup[] = [];
  const seen = new Set<string>();
  let groupId = snapshot.rootQueueId;
  while (groupId && !seen.has(groupId)) {
    const group = groups.get(groupId);
    if (!group) break;
    seen.add(groupId);
    chain.push(group);
    groupId = group.fallbackQueueId || '';
  }
  return chain;
}
